import { Graphics } from "pixi.js";
import type { GameConfig } from "../config/GameConfig";
import { rgbToHex } from "../config/Palette";
import type { WorldBounds } from "../core/WorldBounds";
import type { Divider } from "../entities/Divider";

/**
 * Fundo partido pela costura: linha horizontal ou oval fechada.
 */
export class DividerBackdrop {
  readonly graphics = new Graphics();

  constructor(
    private readonly config: GameConfig,
    private readonly bounds: WorldBounds,
  ) {
    this.graphics.eventMode = "none";
  }

  draw(divider: Divider): void {
    const g = this.graphics;
    g.clear();

    const w = this.bounds.width;
    const h = this.bounds.height;
    const vis = this.config.visual;
    const upper = rgbToHex(vis.upper.bg);
    const lower = rgbToHex(vis.lower.bg);
    const topColor = vis.swapEnvironments ? lower : upper;
    const bottomColor = vis.swapEnvironments ? upper : lower;

    if (divider.isOval) {
      this.drawOval(g, divider, w, h, topColor, bottomColor);
      return;
    }

    const line = this.polyline(divider, w, h);
    if (line.length < 2) return;

    g.moveTo(0, 0);
    g.lineTo(w, 0);
    for (let i = line.length - 1; i >= 0; i--) {
      g.lineTo(line[i].x, line[i].y);
    }
    g.closePath();
    g.fill({ color: topColor });

    g.moveTo(line[0].x, line[0].y);
    for (let i = 1; i < line.length; i++) {
      g.lineTo(line[i].x, line[i].y);
    }
    g.lineTo(w, h);
    g.lineTo(0, h);
    g.closePath();
    g.fill({ color: bottomColor });

    this.strokeSeam(g, line, vis.swapEnvironments ? rgbToHex(vis.lower.accent) : rgbToHex(vis.upper.accent));
  }

  private drawOval(
    g: Graphics,
    divider: Divider,
    w: number,
    h: number,
    inside: number,
    outside: number,
  ): void {
    const ring = this.closedRing(divider);
    if (ring.length < 3) return;

    g.rect(0, 0, w, h);
    g.fill({ color: outside });

    g.moveTo(ring[0].x, ring[0].y);
    for (let i = 1; i < ring.length; i++) g.lineTo(ring[i].x, ring[i].y);
    g.closePath();
    g.fill({ color: inside });

    const vis = this.config.visual;
    this.strokeSeam(
      g,
      ring,
      vis.swapEnvironments ? rgbToHex(vis.lower.accent) : rgbToHex(vis.upper.accent),
    );
  }

  private strokeSeam(g: Graphics, line: { x: number; y: number }[], seam: number): void {
    g.moveTo(line[0].x, line[0].y);
    for (let i = 1; i < line.length; i++) g.lineTo(line[i].x, line[i].y);
    if (line.length > 2) g.lineTo(line[0].x, line[0].y);
    g.stroke({ width: 10, color: seam, alpha: 0.12 });
    g.moveTo(line[0].x, line[0].y);
    for (let i = 1; i < line.length; i++) g.lineTo(line[i].x, line[i].y);
    if (line.length > 2) g.lineTo(line[0].x, line[0].y);
    g.stroke({ width: 2, color: seam, alpha: 0.35 });
  }

  /** Ordena pela posição desenhada, para o polígono não se cruzar. */
  private closedRing(divider: Divider): { x: number; y: number }[] {
    const cx = divider.cx;
    const cy = divider.cy;
    return divider.points
      .map((p) => ({
        x: p.displayX,
        y: p.displayY,
        a: Math.atan2(p.displayY - cy, p.displayX - cx),
      }))
      .sort((a, b) => a.a - b.a);
  }

  private polyline(
    divider: Divider,
    width: number,
    height: number,
  ): { x: number; y: number }[] {
    const pts = divider.points;
    if (pts.length === 0) {
      const y = divider.baseY;
      return [
        { x: 0, y },
        { x: width, y },
      ];
    }

    const line: { x: number; y: number }[] = [];
    const first = pts[0];
    if (first.displayX > 0.5) {
      line.push({ x: 0, y: first.displayY });
    }
    for (const p of pts) {
      line.push({
        x: p.displayX,
        y: Math.max(0, Math.min(height, p.displayY)),
      });
    }
    const last = line[line.length - 1];
    if (last.x < width - 0.5) {
      line.push({ x: width, y: last.y });
    }
    return line;
  }
}
